import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { formatCalendarDateIso } from '../utils/dateFormat';

const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
const publicHolidaysCache = new Map<
  string,
  { dates: Set<string>; fetchedAt: number }
>();

function getCachedHolidays(country: string): Set<string> | null {
  const entry = publicHolidaysCache.get(country);
  if (!entry || Date.now() - entry.fetchedAt > CACHE_TTL_MS) return null;
  return entry.dates;
}

/**
 * Fetches public holiday dates from the backend (Nager.Date) for the given country
 * and returns an isHoliday checker. Single source of truth with backend (e.g. sick leave rules).
 * Results are cached in-memory (1 hour) so multiple components/pages don't trigger extra API calls.
 */
export function usePublicHolidays(country: string | undefined | null): {
  isHoliday: (date: Date) => boolean;
  ready: boolean;
} {
  const [holidayDates, setHolidayDates] = useState<Set<string>>(new Set());
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!country || country.length < 2) {
      queueMicrotask(() => {
        setHolidayDates(new Set());
        setReady(true);
      });
      return;
    }
    const cached = getCachedHolidays(country);
    if (cached) {
      queueMicrotask(() => {
        setHolidayDates(cached);
        setReady(true);
      });
      return;
    }
    const from = new Date();
    from.setFullYear(from.getFullYear() - 1, 0, 1);
    const to = new Date();
    to.setFullYear(to.getFullYear() + 2, 11, 31);
    const fromStr = formatCalendarDateIso(from);
    const toStr = formatCalendarDateIso(to);

    apiService
      .get<{ holidays: { date: string; name: string }[] }>(
        `/System/public-holidays?country=${encodeURIComponent(country)}&from=${fromStr}&to=${toStr}`,
        { skipTokenRefresh: true }
      )
      .then((res) => {
        const list = Array.isArray(res.data?.holidays) ? res.data.holidays : [];
        const dates = new Set(list.map((h) => h.date));
        publicHolidaysCache.set(country, { dates, fetchedAt: Date.now() });
        setHolidayDates(dates);
      })
      .catch(() => setHolidayDates(new Set()))
      .finally(() => setReady(true));
  }, [country]);

  const isHoliday = useCallback(
    (date: Date) => {
      const key = formatCalendarDateIso(date);
      return holidayDates.has(key);
    },
    [holidayDates]
  );

  return { isHoliday, ready };
}
